import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../components';
import ComplianceNotice from '../components/ComplianceNotice';
import './GrowthForecastCalculator.css';

const COPY = {
  en: {
    title: 'Investment Growth Forecast Calculator',
    subtitle:
      'Build educational growth scenarios with uncertainty ranges. Results are illustrative and not guaranteed.',
    labels: {
      initial: 'Initial investment',
      contribution: 'Contribution amount',
      frequency: 'Contribution frequency',
      frequencyBiweekly: 'Biweekly',
      frequencyMonthly: 'Monthly',
      frequencyQuarterly: 'Quarterly',
      frequencyYearly: 'Yearly',
      annualReturn: 'Expected annual return (%)',
      years: 'Time horizon (years)',
      inflation: 'Inflation assumption (%)',
      actionsTitle: 'Scenario actions',
      recalculate: 'Calculate with live model',
      saveScenario: 'Save scenario',
      recalculating: 'Updating...',
      saveSuccess: 'Scenario saved to your planning history.',
      saveLogin: 'Sign in to save scenarios.',
      apiError: 'Live model is temporarily unavailable. Showing local estimate.',
      leadTitle: 'Main takeaway',
      leadBody: 'At the expected-case assumption, your projected future value is',
      expectedCase: 'Expected range (80% interval)',
      worstCase: 'Absolute worst case',
      bestCase: 'Absolute best case',
      caseChanceExpected: 'About 80% chance of finishing between these two values',
      caseChanceWorst: 'Lowest simulated ending value across all generated paths',
      caseChanceBest: 'Highest simulated ending value across all generated paths',
      distributionTitle: 'Distribution of possible outcomes',
      distributionSubtitle: 'Each bar is the probability of finishing in that value range.',
      modelExplainTitle: 'How this was calculated',
      modelExplainBody:
        'We generated many yearly paths. For each year, both return and inflation can move up or down in 0.10% steps around your assumptions. Return variation is centered on your expected return using a typical diversified-market volatility band, while inflation variation is centered on your inflation assumption with a Canada-focused target-range band. We then compound contributions and growth across all years and summarize the full distribution.',
      contributedVsGrowthTitle: 'Contributed amount vs growth',
      nominal: 'Future value',
      contributions: 'Total contributions',
      growth: 'Total growth',
      liveModelTitle: 'Live model comparison',
      liveModelBody:
        'The live model is the backend estimate using monthly-equivalent contributions. Your local result above uses your selected contribution frequency and the full probability simulation.',
      probabilityAxis: 'Probability',
    },
  },
  fr: {
    title: 'Calculateur de projection de croissance des placements',
    subtitle:
      "Creez des scenarios educatifs avec des intervalles d'incertitude. Les resultats sont illustratifs et non garantis.",
    labels: {
      initial: 'Placement initial',
      contribution: 'Montant de cotisation',
      frequency: 'Frequence de cotisation',
      frequencyBiweekly: 'Aux deux semaines',
      frequencyMonthly: 'Mensuel',
      frequencyQuarterly: 'Trimestriel',
      frequencyYearly: 'Annuel',
      annualReturn: 'Rendement annuel attendu (%)',
      years: 'Horizon (annees)',
      inflation: "Hypothese d'inflation (%)",
      actionsTitle: 'Actions de scenario',
      recalculate: 'Calculer avec le modele en ligne',
      saveScenario: 'Enregistrer le scenario',
      recalculating: 'Mise a jour...',
      saveSuccess: 'Scenario enregistre dans votre historique de planification.',
      saveLogin: 'Connectez-vous pour enregistrer vos scenarios.',
      apiError: 'Le modele en ligne est temporairement indisponible. Estimation locale affichee.',
      leadTitle: 'A retenir',
      leadBody: 'Avec le scenario attendu, la valeur future projetee est',
      expectedCase: 'Plage attendue (intervalle de 80 %)',
      worstCase: 'Pire scenario absolu',
      bestCase: 'Meilleur scenario absolu',
      caseChanceExpected: 'Environ 80 % de chance de finir entre ces deux valeurs',
      caseChanceWorst: 'Valeur finale simulee la plus basse parmi tous les parcours generes',
      caseChanceBest: 'Valeur finale simulee la plus elevee parmi tous les parcours generes',
      distributionTitle: 'Distribution des resultats possibles',
      distributionSubtitle: 'Chaque barre represente la probabilite de finir dans cet intervalle.',
      modelExplainTitle: 'Comment les chiffres sont calcules',
      modelExplainBody:
        'Nous avons genere de nombreux parcours annuels. Chaque annee, le rendement et l inflation peuvent varier en increments de 0,10 % autour de vos hypotheses. La variation du rendement est centree sur votre hypothese avec une bande de volatilite de marche diversifiee, et la variation de l inflation est centree sur votre hypothese avec une bande inspiree de la plage cible canadienne. Ensuite, nous composons les cotisations et la croissance sur tout l horizon et resumons la distribution complete.',
      contributedVsGrowthTitle: 'Montant cotise vs croissance',
      nominal: 'Valeur future',
      contributions: 'Total des cotisations',
      growth: 'Croissance totale',
      liveModelTitle: 'Comparaison modele en ligne',
      liveModelBody:
        'Le modele en ligne est une estimation backend avec des cotisations equivalentes mensuelles. Le resultat local ci-dessus respecte votre frequence choisie et la simulation complete des probabilites.',
      probabilityAxis: 'Probabilite',
    },
  },
};

const PERIODS_PER_YEAR = {
  biweekly: 26,
  monthly: 12,
  quarterly: 4,
  yearly: 1,
};

const RETURN_STD_DEV_PCT = 4.0;
const INFLATION_STD_DEV_PCT = 1.0;
const SHOCK_STEP_PCT = 0.1;
const PATH_COUNT = 12000;

const formatMoney = (value, lang) =>
  new Intl.NumberFormat(lang === 'fr' ? 'fr-CA' : 'en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(Number.isFinite(value) ? value : 0);

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const quantile = (sortedValues, q) => {
  if (!sortedValues.length) return 0;
  const index = (sortedValues.length - 1) * q;
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  if (lower === upper) return sortedValues[lower];
  const weight = index - lower;
  return sortedValues[lower] * (1 - weight) + sortedValues[upper] * weight;
};

const buildShockGrid = (stdDevPct, maxSigma = 3) => {
  const maxAbs = Math.max(SHOCK_STEP_PCT, Math.round((stdDevPct * maxSigma) / SHOCK_STEP_PCT) * SHOCK_STEP_PCT);
  const values = [];
  for (let v = -maxAbs; v <= maxAbs + 1e-9; v += SHOCK_STEP_PCT) {
    values.push(Number(v.toFixed(2)));
  }
  return values;
};

const randomFromGrid = (grid) => grid[Math.floor(Math.random() * grid.length)];

const projectDeterministic = ({ initial, contribution, periodsPerYear, annualReturn, years, inflation }) => {
  let balance = initial;
  const totalContributions = initial + contribution * periodsPerYear * years;
  const periodicRate = annualReturn / 100 / periodsPerYear;

  for (let year = 1; year <= years; year += 1) {
    for (let period = 1; period <= periodsPerYear; period += 1) {
      balance = (balance + contribution) * (1 + periodicRate);
    }
  }

  return {
    nominal: balance,
    real: balance / Math.pow(1 + inflation / 100, years),
    contributions: totalContributions,
    growth: balance - totalContributions,
  };
};

const computeForecastModel = ({ initial, contribution, annualReturn, years, inflation, contributionFrequency }) => {
  const periodsPerYear = PERIODS_PER_YEAR[contributionFrequency] || PERIODS_PER_YEAR.monthly;

  const expected = projectDeterministic({
    initial,
    contribution,
    periodsPerYear,
    annualReturn,
    years,
    inflation,
  });

  const returnGrid = buildShockGrid(RETURN_STD_DEV_PCT, 3);
  const inflationGrid = buildShockGrid(INFLATION_STD_DEV_PCT, 3);
  const nominalOutcomes = [];

  for (let path = 0; path < PATH_COUNT; path += 1) {
    let balance = initial;
    let inflationFactor = 1;

    for (let year = 1; year <= years; year += 1) {
      const yearlyReturn = annualReturn + randomFromGrid(returnGrid);
      const yearlyInflation = clamp(inflation + randomFromGrid(inflationGrid), -2, 12);
      const periodicRate = yearlyReturn / 100 / periodsPerYear;

      for (let period = 1; period <= periodsPerYear; period += 1) {
        balance = (balance + contribution) * (1 + periodicRate);
      }

      inflationFactor *= 1 + yearlyInflation / 100;
    }

    nominalOutcomes.push(balance / Math.max(0.0001, inflationFactor) * Math.max(0.0001, inflationFactor));
  }

  const sortedNominal = [...nominalOutcomes].sort((a, b) => a - b);
  const p10 = quantile(sortedNominal, 0.1);
  const p50 = quantile(sortedNominal, 0.5);
  const p90 = quantile(sortedNominal, 0.9);
  const minOutcome = sortedNominal[0] || 0;
  const maxOutcome = sortedNominal[sortedNominal.length - 1] || 0;

  const binCount = 24;
  const floor = quantile(sortedNominal, 0.01);
  const ceil = quantile(sortedNominal, 0.99);
  const width = Math.max(1, (ceil - floor) / binCount);

  const bins = Array.from({ length: binCount }, (_, i) => ({
    i,
    min: floor + i * width,
    max: floor + (i + 1) * width,
    count: 0,
  }));

  sortedNominal.forEach((value) => {
    const clipped = clamp(value, floor, ceil - 0.0001);
    const index = clamp(Math.floor((clipped - floor) / width), 0, binCount - 1);
    bins[index].count += 1;
  });

  const distribution = bins.map((b) => ({
    ...b,
    probability: b.count / sortedNominal.length,
  }));

  return {
    expected,
    p10,
    p50,
    p90,
    minOutcome,
    maxOutcome,
    distribution,
    returnVariationPct: RETURN_STD_DEV_PCT,
    inflationVariationPct: INFLATION_STD_DEV_PCT,
  };
};

const GrowthForecastCalculator = ({ apiBase, authUser }) => {
  const { lang } = useLanguage();
  const c = COPY[lang] || COPY.en;

  const [inputs, setInputs] = useState({
    initial: 15000,
    contribution: 600,
    contributionFrequency: 'monthly',
    annualReturn: 6,
    years: 20,
    inflation: 2,
  });

  const [results, setResults] = useState(null);
  const [liveModelValue, setLiveModelValue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [saveMessage, setSaveMessage] = useState('');

  const localModel = useMemo(
    () =>
      computeForecastModel({
        initial: Number(inputs.initial),
        contribution: Number(inputs.contribution),
        annualReturn: Number(inputs.annualReturn),
        years: Math.max(1, Math.round(Number(inputs.years))),
        inflation: Number(inputs.inflation),
        contributionFrequency: inputs.contributionFrequency,
      }),
    [inputs]
  );

  const maxProbability = results ? Math.max(...results.distribution.map((b) => b.probability), 0.0001) : 0.0001;

  const handleInput = (field, value) => {
    setResults(null);
    setLiveModelValue(null);
    setSaveMessage('');
    setInputs((prev) => ({
      ...prev,
      [field]: field === 'contributionFrequency' ? value : Number(value),
    }));
  };

  const runForecast = async (saveScenario = false) => {
    setResults(localModel);
    if (!apiBase) return;

    setLoading(true);
    setErrorMessage('');
    setSaveMessage('');

    try {
      const periodsPerYear = PERIODS_PER_YEAR[inputs.contributionFrequency] || PERIODS_PER_YEAR.monthly;
      const monthlyEquivalent = Number(inputs.contribution) * (periodsPerYear / 12);

      const payload = {
        initial_investment: Number(inputs.initial),
        monthly_contribution: monthlyEquivalent,
        expected_annual_return_pct: Number(inputs.annualReturn),
        horizon_years: Number(inputs.years),
        inflation_pct: Number(inputs.inflation),
        save_scenario: saveScenario,
      };

      const response = await axios.post(`${apiBase}/planning/forecast`, payload, {
        withCredentials: true,
      });

      const apiScenarios = Array.isArray(response?.data?.scenarios) ? response.data.scenarios : [];
      const moderate =
        apiScenarios.find((row) => String(row.scenario || '').toLowerCase() === 'moderate') ||
        apiScenarios[1] ||
        apiScenarios[0];

      if (moderate && Number.isFinite(Number(moderate.nominal))) {
        setLiveModelValue(Number(moderate.nominal));
      }

      if (response?.data?.saved_forecast_id) {
        setSaveMessage(c.labels.saveSuccess);
      }
    } catch (_error) {
      setErrorMessage(c.labels.apiError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="forecast-root">
      <section className="forecast-hero dashboard-glass">
        <h2>{c.title}</h2>
        <p>{c.subtitle}</p>
      </section>

      <ComplianceNotice compact />

      <section className="forecast-layout">
        <article className="forecast-inputs dashboard-glass">
          <label>
            <span>{c.labels.initial}</span>
            <input type="number" value={inputs.initial} onChange={(e) => handleInput('initial', e.target.value)} min="0" step="100" />
          </label>
          <label>
            <span>{c.labels.contribution}</span>
            <input type="number" value={inputs.contribution} onChange={(e) => handleInput('contribution', e.target.value)} min="0" step="25" />
          </label>
          <label>
            <span>{c.labels.frequency}</span>
            <select value={inputs.contributionFrequency} onChange={(e) => handleInput('contributionFrequency', e.target.value)}>
              <option value="biweekly">{c.labels.frequencyBiweekly}</option>
              <option value="monthly">{c.labels.frequencyMonthly}</option>
              <option value="quarterly">{c.labels.frequencyQuarterly}</option>
              <option value="yearly">{c.labels.frequencyYearly}</option>
            </select>
          </label>
          <label>
            <span>{c.labels.annualReturn}</span>
            <input type="number" value={inputs.annualReturn} onChange={(e) => handleInput('annualReturn', e.target.value)} min="0" max="15" step="0.1" />
          </label>
          <label>
            <span>{c.labels.years}</span>
            <input type="number" value={inputs.years} onChange={(e) => handleInput('years', e.target.value)} min="1" max="50" step="1" />
          </label>
          <label>
            <span>{c.labels.inflation}</span>
            <input type="number" value={inputs.inflation} onChange={(e) => handleInput('inflation', e.target.value)} min="0" max="8" step="0.1" />
          </label>

          <div className="forecast-actions">
            <span>{c.labels.actionsTitle}</span>
            <button type="button" onClick={() => runForecast(false)} disabled={loading}>
              {loading ? c.labels.recalculating : c.labels.recalculate}
            </button>
            <button
              type="button"
              onClick={() => runForecast(true)}
              disabled={loading || !authUser}
              title={!authUser ? c.labels.saveLogin : c.labels.saveScenario}
            >
              {c.labels.saveScenario}
            </button>
          </div>

          {errorMessage && <p className="forecast-feedback error">{errorMessage}</p>}
          {saveMessage && <p className="forecast-feedback success">{saveMessage}</p>}
        </article>

        {results && (
          <article className="forecast-results dashboard-glass">
            <section className="forecast-lead-card">
              <span>{c.labels.leadTitle}</span>
              <h3>{c.labels.leadBody}</h3>
              <strong>{formatMoney(results.p50, lang)}</strong>
              <p>{c.labels.expectedCase}</p>
            </section>

            <section className="forecast-case-grid">
              <div className="forecast-case-card">
                <span>{c.labels.worstCase}</span>
                <strong>{formatMoney(results.minOutcome, lang)}</strong>
                <p>{c.labels.caseChanceWorst}</p>
              </div>
              <div className="forecast-case-card">
                <span>{c.labels.expectedCase}</span>
                <strong>{formatMoney(results.p10, lang)} - {formatMoney(results.p90, lang)}</strong>
                <p>{c.labels.caseChanceExpected}</p>
              </div>
              <div className="forecast-case-card">
                <span>{c.labels.bestCase}</span>
                <strong>{formatMoney(results.maxOutcome, lang)}</strong>
                <p>{c.labels.caseChanceBest}</p>
              </div>
            </section>

            <section className="forecast-panel">
              <h4>{c.labels.distributionTitle}</h4>
              <p>{c.labels.distributionSubtitle}</p>
              <div className="forecast-distribution-chart" role="img" aria-label={c.labels.distributionTitle}>
                {results.distribution.map((bin) => (
                  <div key={bin.i} className="forecast-dist-bar-wrap" title={`${formatMoney(bin.min, lang)} - ${formatMoney(bin.max, lang)} | ${(bin.probability * 100).toFixed(2)}%`}>
                    <div className="forecast-dist-bar" style={{ height: `${Math.max(2, (bin.probability / maxProbability) * 100)}%` }} />
                  </div>
                ))}
              </div>
              <div className="forecast-dist-axis">
                <span>{formatMoney(results.distribution[0]?.min || 0, lang)}</span>
                <span>{c.labels.probabilityAxis}</span>
                <span>{formatMoney(results.distribution[results.distribution.length - 1]?.max || 0, lang)}</span>
              </div>
            </section>

            <section className="forecast-panel">
              <h4>{c.labels.contributedVsGrowthTitle}</h4>
              <div className="forecast-stack-track">
                <div
                  className="forecast-stack-contrib"
                  style={{ width: `${clamp((results.expected.contributions / Math.max(results.expected.nominal, 1)) * 100, 0, 100)}%` }}
                />
                <div
                  className="forecast-stack-growth"
                  style={{ width: `${clamp((results.expected.growth / Math.max(results.expected.nominal, 1)) * 100, 0, 100)}%` }}
                />
              </div>
              <div className="forecast-stack-legend">
                <span>{c.labels.contributions}: <strong>{formatMoney(results.expected.contributions, lang)}</strong></span>
                <span>{c.labels.growth}: <strong>{formatMoney(results.expected.growth, lang)}</strong></span>
                <span>{c.labels.nominal}: <strong>{formatMoney(results.expected.nominal, lang)}</strong></span>
              </div>
            </section>

            <section className="forecast-panel">
              <h4>{c.labels.modelExplainTitle}</h4>
              <p>{c.labels.modelExplainBody}</p>
              <p>
                {lang === 'fr'
                  ? `Parametres utilises : variation rendement = +/- ${results.returnVariationPct.toFixed(2)} %, variation inflation = +/- ${results.inflationVariationPct.toFixed(2)} %, increment = ${SHOCK_STEP_PCT.toFixed(2)} %, simulations = ${PATH_COUNT.toLocaleString('fr-CA')}.`
                  : `Parameters used: return variation = +/- ${results.returnVariationPct.toFixed(2)}%, inflation variation = +/- ${results.inflationVariationPct.toFixed(2)}%, increment = ${SHOCK_STEP_PCT.toFixed(2)}%, simulations = ${PATH_COUNT.toLocaleString('en-CA')}.`}
              </p>
            </section>

            {liveModelValue !== null && (
              <section className="forecast-panel live">
                <h4>{c.labels.liveModelTitle}</h4>
                <strong>{formatMoney(liveModelValue, lang)}</strong>
                <p>{c.labels.liveModelBody}</p>
              </section>
            )}
          </article>
        )}
      </section>
    </div>
  );
};

export default GrowthForecastCalculator;
